import Toast from '@/components/elements/toast'
import Layout from '@/components/layout/layout'
import ProductSimpleSlider from '@/components/sliders/products-simple-slider'
import { FilteredProductsResponse } from '@/entities/api/products/product-filters'
import { filterByPromotions } from '@/services/api/products/products-filter'
import { setPromoProducts } from '@/store/features/products'
import { AppDispatch, RootState } from '@/store/store'
import { useEffect, useState, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Zap, Star,  Eye, Award } from 'lucide-react'
import PromoCountdownBanner from '@/components/heros/promotions-count-hero'
import Guarantees from '@/components/layout/parts/guarantees'
import CustomersEntity from '@/entities/api/customers/customer'

const Promos = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const { promoProducts } = useSelector((state: RootState) => state.product)
	const [isLoading, setIsLoading] = useState(false)
	const [isInitialLoading, setIsInitialLoading] = useState(false)
	const loadingRef = useRef(false)
	const initialLoadRef = useRef(false)

	const dispatch = useDispatch<AppDispatch>()

	const getPromoProducts = useCallback(async () => {
		if (
			promoProducts?.products?.length ||
			initialLoadRef.current ||
			isInitialLoading
		)
			return

		setIsInitialLoading(true)
		initialLoadRef.current = true

		try {
			const response = await filterByPromotions({
				onlyWithDiscount: true,
				page: 1,
				limit: 24,
			})

			if ([400, 401].includes(response?.status ?? 0)) {
				Toast({
					type: 'error',
					message: 'No se pudieron cargar los productos en promoción',
				})
			} else {
				dispatch(setPromoProducts(response?.data ?? { products: [], total: 0 }))
			}
		} catch (error) {
			Toast({
				type: 'error',
				message: 'No se pudieron cargar los productos en promoción',
			})
		} finally {
			setIsInitialLoading(false)
			initialLoadRef.current = false
		}
	}, [promoProducts?.products?.length, isInitialLoading, dispatch])

	useEffect(() => {
		getPromoProducts()
	}, [getPromoProducts])

	const handleLoadMore = useCallback(
		async (nextPage: number): Promise<void> => {
			if (loadingRef.current || isLoading) return

			loadingRef.current = true
			setIsLoading(true)

			try {
				const response = await filterByPromotions({
					onlyWithDiscount: true,
					page: nextPage,
					limit: 24,
				})

				if ([400, 401].includes(response?.status ?? 0)) {
					Toast({
						type: 'error',
						message: 'Error al cargar más productos en promoción',
					})
					return
				}

				if (response?.data) {
					dispatch(
						setPromoProducts({
							...response.data,
							products: [
								...(promoProducts?.products || []),
								...response.data.products,
							],
							currentPage: response.data.currentPage,
						})
					)
				}
			} catch (error) {
				Toast({
					type: 'error',
					message: 'Error al cargar más productos en promoción',
				})
				throw error
			} finally {
				setIsLoading(false)
				loadingRef.current = false
			}
		},
		[isLoading, promoProducts?.products, dispatch]
	)

	return (
		<Layout customer={customer} token={token}>
			<div className='w-full px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8'>
				<PromoCountdownBanner />

				<div className='mb-6 lg:mb-8'>
					<div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
						<div>
							<h2 className='text-2xl sm:text-3xl lg:text-4xl font-black text-gray-800 mb-2'>
								Productos en <span className='text-red-600'>Promoción</span>
							</h2>
							<p className='text-gray-600 text-sm sm:text-base'>
								Los mejores descuentos en productos premium seleccionados
								especialmente para ti
							</p>
						</div>
						<div className='flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2 rounded-xl shadow-lg'>
							<Star className='w-4 h-4 fill-current' />
							<span className='font-bold text-sm'>Exclusivo</span>
						</div>
					</div>
				</div>

				<div className='w-full flex flex-col relative'>
					<div className='flex justify-center items-start'>
						<div className='w-full max-w-none px-0 md:px-2 lg:px-4 xl:px-8'>
							<div className='w-full flex relative'>
								<div className='w-full flex flex-col min-w-0'>
									<div className='space-y-4 sm:space-y-6 lg:space-y-8'>
										<ProductSimpleSlider
											filteredProducts={
												promoProducts as FilteredProductsResponse
											}
											customerId={customer?._id || ''}
											customer={customer}
											enableInfiniteScroll={true}
											onLoadMore={handleLoadMore}
											isLoading={isLoading}
										/>
									</div>

									<div className='mt-12 lg:mt-16'>
										<div className='bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-6 lg:p-8 text-white text-center shadow-2xl'>
											<div className='max-w-2xl mx-auto'>
												<div className='bg-white/20 rounded-full p-3 w-fit mx-auto mb-4'>
													<Zap className='w-6 h-6 text-white' />
												</div>
												<h3 className='text-xl sm:text-2xl lg:text-3xl font-black mb-3'>
													¿No encontraste lo que buscabas?
												</h3>
												<p className='text-red-100 mb-6 text-sm sm:text-base'>
													Explora nuestro catálogo completo con más de 500
													productos deportivos
												</p>
												<div className='flex flex-col sm:flex-row gap-4 justify-center items-center'>
													<Link
														to='/'
														className='bg-white text-red-600 font-bold px-6 py-3 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center gap-2'
													>
														<Eye className='w-5 h-5' />
														Ver Todas las Categorías
													</Link>
													<div className='flex items-center gap-2 text-red-100'>
														<Award className='w-4 h-4' />
														<span className='text-sm font-medium'>
															Calidad garantizada
														</span>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<Guarantees />
			</div>
		</Layout>
	)
}

export default Promos
